'use strict';

import {Buildings, components, isMobile, projectMode, settingsData} from '../../../settings';
import {
	applyFacingRules,
	filterDuplicatesFromAllCrmData, getDataForChecked,
	selectedBuildings,
	updateSearchResults,
} from './search';
import { isOrbitControlsEnabled } from '../../controls/orbitControls';
import { changeLangToCurrent, getCurrentLang } from '../../components/langSwitcher/langSwitcher';
import { BaseElement } from '../../interfaces/BaseElement';
import { getTowerName } from '../../../functions';
import { MultipleSelector } from '../../interfaces/MultipleSelector';

export function createMultipleSearch () {
	if (config.features.privateHouseMode)
		return;

	if (Buildings.getBuildingsInfoLength() < 2) {
		Buildings.getBuildingsArray().forEach((building) => {
			if (Object.keys(building.info).length !== 0)
				selectedBuildings.add(building.name);
		});
		return;
	}

	const mainContainerParent = isMobile
		? document.querySelector('.mobile-search__filter-pop-up')
		: document.querySelector('.search__result-wrapper');
	const mainContainer = new BaseElement()
		.addClass('multiple__search__container')
		.event('click', multiSearchHandler)
		.appendTo(mainContainerParent);


	if (isMobile) {
		mainContainer
			.addClass('search__option')
			.addAttribute('state', 'inactive');

		new BaseElement()
			.addClass('search__option-title', 'language-string')
			.addAttribute('data-dictionary', 'Buildings')
			.innerHtml(changeLangToCurrent('Buildings'))
			.appendTo(mainContainer.DOMElement);
	} else {
		// const elementTitle = new BaseElement()
		// 	.appendTo(mainContainerParent);
		new BaseElement('span')
			.addClass('language-string', 'search__option-title--text', 'text', 'callout')
			.addAttribute('data-dictionary', 'Buildings')
			.innerHtml(changeLangToCurrent('Buildings'))
			.appendTo(mainContainer.DOMElement);
	}
	
	const contentContainer = !isMobile && new BaseElement()
		.addClass('multiple__result__content')
		.appendTo(mainContainer.DOMElement);

	const searchWrapper = isMobile && new BaseElement()
		.addClass('search__wrapper')
		.appendTo(mainContainer.DOMElement);

	const buildings = Buildings.getBuildingsArray();
	if (!isMobile && buildings.length > 6) {
		const settings = config.features.search.multipleSearch || {useGroups: true};

		MultipleSelector.dispose('multipleSearch');

		mainContainer.addClass('multiple__select__main-container');
		mainContainer.addClass('multiple__search__container--row');

		const select = new BaseElement('select')
			.addAttribute('multiple', 'multiple')
			.addClass('multiple__select', 'multiple-select')
			.appendTo(isMobile ? searchWrapper.DOMElement : contentContainer.DOMElement)
			.DOMElement;

		const lots = [...new Set(buildings.map(building => building.info?.buildingLot))].filter(el => el && el.length);

		if (lots.length) {
			lots.forEach(lot => {
				let parent;

				if (settings.useGroups) {
					parent = new BaseElement('optgroup')
						.addClass('multiple__select__group')
						.addAttribute('label', lot)
						.addAttribute('data-lot', lot)
						.appendTo(select)
						.DOMElement;
				} else {
					parent = select;
				}

				const lotBuildings = buildings.filter(building => building.info.buildingLot === lot);
				lotBuildings.forEach(building => {
					let towerName = getTowerName(building);
					const buildingName = new BaseElement('option')
						.addClass('multiple__select__option', settings.useGroups ? 'multiple__select__child-option' : null)
						.innerHtml(towerName)
						.addAttribute('data-tower-name', towerName)
						.addAttribute('data-building', building.name)
						.appendTo(parent);

					if (Array.isArray(building.info.towerName)) {
						building.info.towerName.forEach(language => {
							buildingName.addAttribute(`data-lang-${language.lang}`, language.value);
						});
					}
				});
			});
		} else {

			buildings.forEach(building => {
				if (Object.keys(building.info).length === 0)
					return;
				let towerName = getTowerName(building);
				const buildingName = new BaseElement('option')
					.addClass('multiple__select__option')
					.innerHtml(towerName)
					.addAttribute('data-tower-name', towerName)
					.addAttribute('data-building', building.name)
					.appendTo(select);

                if (Array.isArray(building.info.towerName)) {
                    building.info.towerName.forEach(language => {
                        buildingName.addAttribute(`data-lang-${language.lang}`, language.value);
                    });
                }

			});
		}

		const multipleSelector = new MultipleSelector(select, 'multipleSearch', {
			showClear: true,
			selectAll: false,
			animate: 'fade',
			minimumCountSelected: 100000,
		});

		multipleSelector.minSelected = isMobile ? 1 : 2;

		multipleSelector.addCallback('onCheckAll', (scope) => {
			scope.data.forEach(obj => {
				if (obj.type === 'optgroup') {
					obj.children.forEach(b => {
						selectedBuildings.add(b._data.building);
					});
				} else {
					selectedBuildings.add(obj._data.building);
				}
			});
			updateSearchResults();
			// FiltersCounter.update();
		});

		multipleSelector.addCallback('onUncheckAll', (scope) => {
			scope.data.forEach(obj => {
				if (obj.type === 'optgroup') {
					obj.children.forEach(b => {
						selectedBuildings.delete(b._data.building);
					});
				} else {
					selectedBuildings.delete(obj._data.building);
				}
			});
			updateSearchResults();
			// FiltersCounter.update();
		});

		multipleSelector.addCallback('onOptgroupClick', (scope, view) => {
			if (view.selected) {
				view.children.forEach(b => {
					selectedBuildings.add(b.data.building);
				});
			} else {
				view.children.forEach(b => {
					selectedBuildings.delete(b.data.building);
				});
			}
			updateSearchResults();
			// FiltersCounter.update();
		});

		multipleSelector.addCallback('onClick', (scope, view) => {
			if (view.selected) {
				selectedBuildings.add(view.data.building);
			} else {
				selectedBuildings.delete(view.data.building);
			}
			updateSearchResults();
			// FiltersCounter.update();
		});

		multipleSelector.addCallback('onUpdatePlaceholder', (placeholder, selected) => {
			const selectedItems = selected.size;
			if (selectedItems === 0) {
				placeholder.text(changeLangToCurrent('buildingsSelect'));
			} else {
				const currentLang = getCurrentLang();
				const key = 'lang' + currentLang.charAt(0).toUpperCase() + currentLang.slice(1,2);
				const data = [...selected].map(el => el[key]).slice(selectedItems - multipleSelector.minSelected, selectedItems);
				if (data.length > 1) {
					placeholder.text(data.join(' | ').toString());
				} else {
					placeholder.text(data.join('').toString());
				}
			}
		});

		multipleSelector.addCallback('onLanguageUpdate',(selectHTML,selected) => {
			const currentLang = getCurrentLang();
			const lang = currentLang.charAt(0).toUpperCase() + currentLang.slice(1,2);
			selected.forEach(data => {
				data.towerName = data[`lang${lang}`];
			});

			const elements = selectHTML.querySelectorAll('option');
			let translate;
			[...elements].forEach(el => {
				translate = el.getAttribute(`data-lang-${currentLang}`);
				if (translate) {
					el.innerHTML = translate;
					el.setAttribute('data-tower-name',translate);
				}
			});
		});
		
		if (selectedBuildings.size > 0) {
			selectedBuildings.forEach((name) => {
				const building = Buildings.getBuilding(name);
				const towerName = getTowerName(building);
				multipleSelector.setSelect(towerName);
			});
		}

	} else {
		const multisearchData = getMultisearchData();
		buildings.forEach((building) => {
			if (Object.keys(building.info).length === 0)
				return;

			const amountOfFlats = parseInt(getAmountOfFoundFlats(multisearchData, building), 10);
			if (amountOfFlats === 0)
				return;

			let towerName = getTowerName(building);

			const resultContainer = new BaseElement()
				.addClass('multiple__result__container')
				.addAttribute('data-tower-name', towerName)
				.addAttribute('data-building', building.name)
				.appendTo(isMobile ? searchWrapper.DOMElement : contentContainer.DOMElement);
			
			if (selectedBuildings.has(building.name)) {
				resultContainer.addClass('active-multi-building');
			}

			if (isMobile) {
				const buildingNameWrap = new BaseElement()
					.addClass('multiple__building-wrap')
					.appendTo(resultContainer.DOMElement);
				const buildingNameElement = new BaseElement('span')
					.addClass('multiple__building-name', 'text', 'callout')
					.innerHtml(towerName)
					.appendTo(buildingNameWrap.DOMElement);
				if (Array.isArray(building.info.towerName)) {
					buildingNameElement.addClass('language-string');
					building.info.towerName.forEach(language => {
						buildingNameElement.addAttribute(`data-lang-${language.lang}`, language.value);
					});
				}

			} else {
				const buildingNameWrap = new BaseElement()
					.addClass('multiple__building-wrap')
					.appendTo(resultContainer.DOMElement);
				const buildingNameElement = new BaseElement('span')
					.addClass('multiple__building-name')
					.innerHtml(towerName)
					.appendTo(buildingNameWrap.DOMElement);
				if (Array.isArray(building.info.towerName)) {
					buildingNameElement.addClass('language-string');
					building.info.towerName.forEach(language => {
						buildingNameElement.addAttribute(`data-lang-${language.lang}`, language.value);
					});
				}
				new BaseElement('span')
					.addClass('multiple__result__count')
					.innerHtml(` (${amountOfFlats})`)
					.appendTo(buildingNameWrap.DOMElement);
			}
		});
	}


	const amountOfPresentBuildings = [...document.querySelectorAll('.multiple__result__container')].length;
	if (amountOfPresentBuildings === 1)
		mainContainer.remove();
}

function getMultisearchData () {
	let {allCrmData, searchState} = settingsData;
	let filteredData = [...filterDuplicatesFromAllCrmData(allCrmData)];

	for (let state of searchState) {
		filteredData = filteredData.filter(dataItem => {
			let currentStateDataValue = dataItem[state.type];

			if (state.type === 'facing')
				currentStateDataValue = applyFacingRules(currentStateDataValue);

			if (dataItem.status === 'Available' && ['', undefined, null].indexOf(currentStateDataValue) === -1) {
				if (state.checked)
					return getDataForChecked(dataItem, currentStateDataValue, state);

				if (state.min <= currentStateDataValue && currentStateDataValue <= state.max)
					return dataItem;
			}
		});
	}

	return filteredData;
}

export function updateMultiSearch () {
	if (Buildings.getBuildingsInfoLength() < 2)
		return;
	
	const multisearchData = getMultisearchData();

	Buildings.getBuildingsArray().forEach(building => {
		if (Object.keys(building.info).length === 0)
			return;

		const towerName = getTowerName(building);

		const htmlElement = document.querySelector(`.multiple__result__container[data-tower-name="${towerName}"`);
		if (htmlElement) {
			const count = htmlElement.querySelector('.multiple__result__count');
			if (count)
				count.textContent = ` (${getAmountOfFoundFlats(multisearchData, building)})`;
		}
	});
}

function getAmountOfFoundFlats (searchResult, building) {
	let amount = 0;

	searchResult.forEach(element => {
		if (element.buildingEngineId === building.name) amount++;
	});

	return amount.toString();
}

async function multiSearchHandlerThrottling (e) {
	const currentTarget = e.target.closest('[data-tower-name]');
	if (Boolean(currentTarget))
		toggleMultiSearchButton(currentTarget);
	updateMultiSearch();
	updateSearchResults();
	updateMultiSearchState();
	// FiltersCounter.update();
	if (isMobile)
		components.mobileSearch.predict();
}

export function updateMultiSearchState () {
	const multipleSearchRootElement = document.querySelector('.multiple__search__container');
	if (!multipleSearchRootElement)
		return;

	const multipleSearchContainers = [...multipleSearchRootElement.getElementsByClassName('multiple__result__container')];
	for (let container of multipleSearchContainers) {
		if (container.classList.contains('active-multi-building')) {
			multipleSearchRootElement.setAttribute('state', 'active');
			return;
		}
	}
	multipleSearchRootElement.setAttribute('state', 'inactive');
}

export function toggleMultiSearchButton (target) {
	const buildingName = target.getAttribute('data-building');
	if (selectedBuildings.has(buildingName)) {
		selectedBuildings.delete(buildingName);
		target.classList.toggle('active-multi-building');
	} else {
		selectedBuildings.add(buildingName);
		target.classList.toggle('active-multi-building');
	}
}

let clickTourTimer = null;

function multiSearchHandler (e) {
	if (!isOrbitControlsEnabled() && !projectMode.LiteMode.modeIs('picture') && !projectMode.LiteMode.modeIs('empty'))
		return;

	if (clickTourTimer !== null)
		clearTimeout(clickTourTimer);

	clickTourTimer = setTimeout(() => multiSearchHandlerThrottling(e), 300);
}
